/**
 * Public voting page (/v/:electionId), opened from the QR on the badge or on the screen. No login:
 * CI + PIN (or the badge QR) → short session → ballot. Elderly-friendly: big type, one thing at a time.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, ScanLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { QrScanner } from '@/features/events/checkin/components/QrScanner';
import { publicBallot, publicCast, publicIdentify } from '../data/votingRepository';
import { BallotCandidateCard } from '../components/BallotCandidateCard';
import { ConfirmVoteModal } from '../components/ConfirmVoteModal';
import { RoundCountdown } from '../components/RoundCountdown';
import type { PublicBallot } from '../types';
import { REASON_LABEL } from '../utils/labels';
import { identifyErrorMessage } from '../utils/identifyMessages';
import { badgeTokenFromScan } from '../utils/publicUrl';
import { votingErrorMessage } from '../utils/votingErrors';

const sessionKey = (electionId: string) => `voting-session:${electionId}`;

function readSession(electionId: string): string | null {
  try {
    return sessionStorage.getItem(sessionKey(electionId));
  } catch {
    return null;
  }
}
function writeSession(electionId: string, token: string | null) {
  try {
    if (token) sessionStorage.setItem(sessionKey(electionId), token);
    else sessionStorage.removeItem(sessionKey(electionId));
  } catch {
    // storage unavailable (private mode): the session lives only in memory
  }
}

export function PublicVotePage() {
  const { electionId = '' } = useParams<{ electionId: string }>();
  const [session, setSession] = useState<string | null>(() => readSession(electionId));

  const setAndStore = (token: string | null) => {
    writeSession(electionId, token);
    setSession(token);
  };

  return (
    <div className="mx-auto min-h-screen max-w-3xl p-4 sm:p-6">
      {session ? (
        <Ballot session={session} onExit={() => setAndStore(null)} />
      ) : (
        <Identify electionId={electionId} onSession={setAndStore} />
      )}
    </div>
  );
}

// ─── Identify ────────────────────────────────────────────────────────────────

function Identify({ electionId, onSession }: { electionId: string; onSession: (t: string) => void }) {
  const [ci, setCi] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  const submit = async (badgeToken?: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await publicIdentify({ electionId, ci: ci.trim(), pin: pin.trim() || undefined, badgeToken });
      if (r.ok) onSession(r.session_token);
      else setError(identifyErrorMessage(r));
    } catch (err) {
      const offline = err instanceof TypeError || /fetch|network/i.test(String(err));
      setError(offline ? 'Sin conexión. Verifique la señal e intente de nuevo.' : votingErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const canSubmit = ci.replace(/\D/g, '').length >= 5 && pin.trim().length >= 4;

  return (
    <div className="flex min-h-[80vh] flex-col justify-center gap-6">
      <div className="text-center">
        <h1 className="text-4xl font-bold">Votación</h1>
        <p className="mt-2 text-xl text-muted-foreground">Ingrese con su carnet de identidad y el PIN de su credencial</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-1">
          <label htmlFor="v-ci" className="text-xl font-medium">Carnet de identidad (CI)</label>
          <Input id="v-ci" className="h-16 text-2xl" inputMode="text" autoComplete="off" value={ci} onChange={(e) => setCi(e.target.value)} placeholder="Ej. 1234567" />
        </div>
        <div className="space-y-1">
          <label htmlFor="v-pin" className="text-xl font-medium">PIN de su credencial</label>
          <Input
            id="v-pin"
            className="h-16 text-center text-3xl tracking-[0.4em]"
            inputMode="numeric"
            autoComplete="off"
            maxLength={6}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            placeholder="••••••"
          />
        </div>

        {error && <p className="rounded-xl bg-destructive/10 p-4 text-center text-xl text-destructive">{error}</p>}

        <Button className="h-16 w-full text-xl" disabled={!canSubmit || busy} onClick={() => void submit()}>
          {busy && <Loader2 className="mr-2 size-5 animate-spin" />} Ingresar
        </Button>
        <Button
          variant="outline"
          className="h-14 w-full text-lg"
          disabled={busy || ci.replace(/\D/g, '').length < 5}
          onClick={() => setScanning(true)}
        >
          <ScanLine className="mr-2 size-5" /> Escanear el QR de mi credencial (sin PIN)
        </Button>
        {ci.replace(/\D/g, '').length < 5 && <p className="text-center text-sm text-muted-foreground">Escriba primero su CI.</p>}
      </div>

      {scanning && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/90 p-4">
          <p className="py-3 text-center text-xl text-white">Apunte la cámara al QR de su credencial</p>
          <div className="mx-auto w-full max-w-md flex-1">
            <QrScanner
              onScan={(text) => {
                const t = badgeTokenFromScan(text);
                if (!t) return;
                setScanning(false);
                void submit(t);
              }}
            />
          </div>
          <Button variant="secondary" className="mx-auto my-4 h-14 px-8 text-lg" onClick={() => setScanning(false)}>Cancelar</Button>
        </div>
      )}
    </div>
  );
}

// ─── Ballot ──────────────────────────────────────────────────────────────────

function Ballot({ session, onExit }: { session: string; onExit: () => void }) {
  const qc = useQueryClient();
  const [choice, setChoice] = useState<{ id: string | null; name: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justVoted, setJustVoted] = useState(false);

  const { data, error: loadError } = useQuery({
    queryKey: ['voting', 'public-ballot', session],
    queryFn: async () => ({ ballot: await publicBallot(session), fetchedAt: Date.now() }),
    refetchInterval: 4000,
    retry: 1,
  });

  // The session expired or is unknown: start over
  useEffect(() => {
    if (loadError && /session_expired/.test(String(loadError))) onExit();
  }, [loadError, onExit]);

  const ballot: PublicBallot | undefined = data?.ballot;
  const raceId = ballot?.race?.id;

  useEffect(() => {
    setJustVoted(false);
    setChoice(null);
    setError(null);
  }, [raceId]);

  const vote = async (candidateId: string | null) => {
    if (!ballot?.race || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await publicCast(session, ballot.race.id, candidateId);
      setChoice(null);
      setJustVoted(true);
      await qc.invalidateQueries({ queryKey: ['voting', 'public-ballot', session] });
    } catch (err) {
      setChoice(null);
      const offline = err instanceof TypeError || /fetch|network/i.test(String(err));
      if (/session_expired/.test(String(err))) return onExit();
      setError(offline ? 'Sin conexión. Su voto aún no se registró: verifique la señal e intente de nuevo.' : votingErrorMessage(err));
      void qc.invalidateQueries({ queryKey: ['voting', 'public-ballot', session] });
    } finally {
      setSubmitting(false);
    }
  };

  if (!ballot) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center">
        {loadError ? <p className="text-center text-xl text-destructive">{votingErrorMessage(loadError)}</p> : <Loader2 className="size-10 animate-spin text-muted-foreground" />}
      </div>
    );
  }

  const header = (
    <div className="mb-4 flex items-center justify-between gap-2 text-sm text-muted-foreground">
      <span>{ballot.election_name} · <strong className="text-foreground">{ballot.full_name}</strong></span>
      <Button variant="ghost" size="sm" onClick={onExit}>Salir</Button>
    </div>
  );

  if (ballot.blocked_reason) {
    return (
      <>
        {header}
        <Notice title="No puede votar" text={REASON_LABEL[ballot.blocked_reason] ?? 'Usted no puede votar en esta elección.'} />
      </>
    );
  }

  if (justVoted || ballot.already_voted) {
    return (
      <>
        {header}
        <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 text-center">
          <CheckCircle2 className="size-28 text-green-600" />
          <h1 className="text-4xl font-bold">{justVoted ? 'Voto registrado' : 'Ya votó en esta ronda'}</h1>
          <p className="text-xl text-muted-foreground">Gracias. Espere la siguiente votación: esta pantalla se actualiza sola.</p>
        </div>
      </>
    );
  }

  if (!ballot.race) {
    return (
      <>
        {header}
        <Notice title="No hay votación abierta" text="Espere: esta pantalla se actualiza sola cuando se abra una ronda." />
      </>
    );
  }

  const race = ballot.race;
  return (
    <>
      {header}
      <div className="space-y-5">
        <div className="text-center">
          <h1 className="text-4xl font-bold">{race.position_title}</h1>
          <p className="text-xl text-muted-foreground">Ronda {race.round_number} · Toque la foto de su candidato</p>
          {race.closes_at && (
            <p className="mt-1"><RoundCountdown closesAt={race.closes_at} serverNow={ballot.server_now} fetchedAt={data?.fetchedAt ?? Date.now()} /></p>
          )}
        </div>
        {error && <p className="rounded-xl bg-destructive/10 p-4 text-center text-xl text-destructive">{error}</p>}
        <div className="grid gap-4 sm:grid-cols-2">
          {race.candidates.map((c) => (
            <BallotCandidateCard
              key={c.id}
              name={c.full_name ?? '—'}
              photoFilename={c.photo_filename}
              disabled={submitting}
              onSelect={() => setChoice({ id: c.id, name: c.full_name ?? '—' })}
            />
          ))}
          {race.allow_blank && (
            <BallotCandidateCard blank name="Voto en blanco" disabled={submitting} onSelect={() => setChoice({ id: null, name: 'Voto en blanco' })} />
          )}
        </div>
      </div>
      {choice && (
        <ConfirmVoteModal choiceName={choice.name} submitting={submitting} onConfirm={() => void vote(choice.id)} onCancel={() => setChoice(null)} />
      )}
    </>
  );
}

function Notice({ title, text }: { title: string; text: string }) {
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center gap-3 text-center">
      <h1 className="text-3xl font-bold">{title}</h1>
      <p className="text-xl text-muted-foreground">{text}</p>
    </div>
  );
}
